import React from 'react'
import { Link, withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import styles from './UserProfilePage.scss'
import { UserBannerCard } from './UserBannerCard'
import { PrivateUserProfilePage, FriendsOnlyUserProfilePage } from './PrivateUserPage'
import { StatsWidget, getCareerStats } from 'features/homepage/StatsWidget'
import { FollowsCard } from 'features/homepage/Feed/FollowsCard'
import { MiniGoalCard } from 'features/homepage/Dashboard/Goals'
import { showGallery } from 'features/gallery/showGallery'
import { NoticeBar } from 'common/styleguide/NoticeBar'
import { ColWrap, MainCol, SecondaryCol } from 'common/layouts/StandardLayout/TwoCol'
import { Loadable } from 'common/Loadable'
import { Card } from 'common/styleguide/Card'
import { TRouteItem } from 'common/TRouteItem'
import { Image } from 'common/Image'
import { LoginModalOverlay } from 'common/styleguide/LoginModalOverlay'
import { IntroWidget } from 'common/IntroWidget'
import { SEND_MESSAGE_DIALOG, SendMessage } from 'common/dialogs/SendMessage'
import { CONFIRM_REMOVE_FRIEND_DIALOG, ConfirmRemoveFriend } from 'common/dialogs/ConfirmRemoveFriend'
import { AdminMenuItem, AdminMenuSeparator } from 'common/GlobalNav/AdminMenu'
import { RouteRideList } from 'common/RouteRideList'
import { ACCOUNT_MERGE_MODAL } from 'common/modals/AccountMergeModal'
import { followActions, followSelectors } from 'data/resources/follows'
import { userActions, userSelectors } from 'data/resources/users'
import { subscriptionSelectors } from 'data/resources/subscriptions'
import { promiseMapAll } from 'lib/utils'
import { showModal } from 'lib/modalUtils'
import fetchJSON from 'lib/fetchJSON'
import { apiv3 } from 'lib/apiv3'
import { Msg, featureNames, commonNouns } from 'lib/localization'
import Immutable, { Map } from 'immutable'
import _ from 'lodash'
import { FormattedMessage as T, defineMessages } from 'react-intl'

// admin dialogs
import {
  USER_INFO_DIALOG, UserInfo,
  USER_LOGIN_HISTORY_DIALOG, UserLoginHistory,
  MOONIFY_USER_DIALOG, MoonifyUser,
  FLAG_USER_AS_IMPORTANT_DIALOG, FlagUserAsImportant,
  UNAUTHENTICATE_USER_DIALOG, UnauthenticateUser,
  USER_DEACTIVATION_DIALOG, UserDeactivationDialog
} from 'common/dialogs/AdminActionDialogs'

const DEFAULT_STATS_INTERVAL = 'year'

const MODAL_LOGIN = 'MODAL_LOGIN'

const messages = defineMessages({
  total: {
    id: 'UserProfilePage.total',
    defaultMessage: '{count} total'
  },
  follows: {
    id: 'UserProfilePage.follows',
    defaultMessage: 'Follows',
    description: 'Noun: referring to following + followers'
  }
})

class _UserProfilePage extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      cachedTRoutes: null,
      modal: null
    }
  }

  componentDidMount () {
    this.showGalleryIfPhotoInURL()
  }

  componentDidUpdate (prevProps) {
    const { recentTRoutes } = this.props.loadableResources
    if (recentTRoutes !== prevProps.loadableResources.recentTRoutes) {
      // Blow out the local cache if this component somehow receives new results from loadable
      this.setState({ cachedTRoutes: null })
    }
  }

  showGalleryIfPhotoInURL () {
    const galleryIsOpen = document.getElementById('#react-gallery') // again, I never said it was elegant
    if (this._didShowGallery || galleryIsOpen) { return } // use a flag to prevent weird state clobbering

    const { user } = this.props
    const { photos } = this.props.loadableResources
    if (user && photos && window.location.hash.match(/#photos\/\d+$/)) {
      this._didShowGallery = true
      const activePhotoId = parseInt(window.location.hash.match(/#photos\/(\d+)$/)[1])
      const activePhoto = photos.find(p => p.id === activePhotoId)
      if (!activePhoto) { return }
      const orderedPhotos = _.orderBy(photos, 'id', 'desc')
      showGallery({
        photos: orderedPhotos,
        activePhotoId,
        readonly: true
      })
    }
  }

  showLoginModal = () => {
    this.setState({ modal: MODAL_LOGIN })
  }

  clearModal = () => {
    this.setState({ modal: null })
  }

  showDialog = (dialog) => {
    this.setState({ modal: dialog })
  }

  requestFriend = () => {
    const { user } = this.props
    const { currentUser, befriendUser } = this.props
    befriendUser(currentUser.id, user.id)
  }

  confirmFriend = () => {
    const { confirmFriendRequest, currentUser, user } = this.props
    confirmFriendRequest(currentUser.id, user.get('id'))
  }

  onTRouteResults = (search) => {
    if (search.params.keywords === '') {
      this.setState({ cachedTRoutes: search })
    }
  }

  onDeleteResult = (record, callback) => {
    this.setState((state, props) => {
      const troutes = state.cachedTRoutes || props.loadableResources.recentTRoutes

      const newTRoutes = Object.assign({}, troutes, { results: _.reject(troutes.results, (r) => r === record) })
      return {
        cachedTRoutes: newTRoutes
      }
    }, callback)
  }

  render () {
    const { modal, cachedTRoutes } = this.state
    const {
      currentUser,
      user,
      following,
      followers,
      currentUserIsFriend,
      subscription
    } = this.props
    // TODO - source this data through Redux
    const { careerStats, recentTRoutes, orgs, goals, photos, isLiveLogging } = this.props.loadableResources
    const currentUserIsUser = currentUser.id === user.id
    const troutes = cachedTRoutes || recentTRoutes

    // we don't want to show the UserProfilePage for a club user. the backend
    // normally redirects get /users/:clubUserId to the orgs layout, but in the
    // case that we spa transition to this component, and we don't have the
    // context where we're rendering the <Link />, this is our final guard
    if (user.accountLevel === 3) {
      window.location.reload()
      return <></>
    }

    return (
      <>
        {isLiveLogging && <NoticeBar className={styles.LiveLogNoticeBar}>{user.name} is currently live logging. <Link to={`/users/${user.id}/live_log`}>Click here to view</Link>.</NoticeBar>}
        <ColWrap>
          {modal === MODAL_LOGIN && <LoginModalOverlay onRequestClose={this.clearModal} />}
          {modal === USER_INFO_DIALOG && <UserInfo onRequestClose={this.clearModal} user={user} />}
          {modal === USER_LOGIN_HISTORY_DIALOG && <UserLoginHistory onRequestClose={this.clearModal} userId={user.id} />}
          {modal === MOONIFY_USER_DIALOG && <MoonifyUser onRequestClose={this.clearModal} userId={user.id} />}
          {modal === FLAG_USER_AS_IMPORTANT_DIALOG && <FlagUserAsImportant onRequestClose={this.clearModal} userId={user.id} />}
          {modal === UNAUTHENTICATE_USER_DIALOG && <UnauthenticateUser onRequestClose={this.clearModal} userId={user.id} />}
          {modal === SEND_MESSAGE_DIALOG && <SendMessage toUser={user} currentUserId={currentUser.id} onRequestClose={this.clearModal} />}
          {modal === CONFIRM_REMOVE_FRIEND_DIALOG && <ConfirmRemoveFriend toUnfriend={user} onRequestClose={this.clearModal} />}
          {modal === USER_DEACTIVATION_DIALOG && <UserDeactivationDialog onRequestClose={this.clearModal} userId={user.id} currentUserId={currentUser.id} />}
          <AdminMenuItem
            menuKey='login_as'
            label='Log In As'
            onClick={() => fetchJSON(`/users/login_as`, { login_as: { user_id: user.id } }, { method: 'POST' }).then(() => { window.location = '/' })} />
          <AdminMenuItem
            menuKey='user_info'
            label='User Info'
            onClick={() => this.setState({ modal: USER_INFO_DIALOG })} />
          <AdminMenuItem
            menuKey='api_history'
            label='API History'
            href={`/users/${user.id}/api_history`} />
          <AdminMenuItem
            menuKey='user_login_history'
            label='Login History'
            onClick={() => this.setState({ modal: USER_LOGIN_HISTORY_DIALOG })} />
          {subscription && <AdminMenuItem
            menuKey='edit_subscription'
            label='Edit Subscription'
            href={`/users/${user.id}/subscription`} />}
          <AdminMenuItem
            menuKey='device_logs'
            label='Show Device Logs'
            href={`/users/${user.id}/device_logs`} />
          <AdminMenuItem
            menuKey='server_notifications'
            label='Server Notifications'
            href={`/users/${user.id}/server_notifications`} />
          <AdminMenuSeparator />
          <AdminMenuItem
            menuKey='moonify_user'
            label='Moonify'
            onClick={() => this.setState({ modal: MOONIFY_USER_DIALOG })} />
          <AdminMenuItem
            menuKey='trial'
            label='Give/Manage Trial'
            href={`/users/${user.id}/new_premium_trial`} />
          <AdminMenuItem
            menuKey='remove_trial_banner'
            label='Remove trial banner'
            onClick={() => fetchJSON(`/users/${user.id}/clear_trial_banner.json`, null, { method: 'POST' }).then(() => { window.alert('Trial banner removed.') })} />
          <AdminMenuItem
            menuKey='account_merge'
            label='Merge into another user'
            onClick={() => showModal(ACCOUNT_MERGE_MODAL, { fromUser: user })} />
          <AdminMenuItem
            menuKey='deactivate_user'
            label='Deactivate'
            onClick={() => this.setState({ modal: USER_DEACTIVATION_DIALOG })} />
          <AdminMenuItem
            menuKey='view_scheduled_deletes'
            label='View Items Scheduled for Deletion'
            href={`/users/${user.id}/deleted_items`} />
          <AdminMenuItem
            menuKey='unauthenticate_user'
            label='Un-authenticate'
            onClick={() => this.setState({ modal: UNAUTHENTICATE_USER_DIALOG })} />
          <AdminMenuSeparator />
          <AdminMenuItem
            menuKey='new_invoice'
            label='Create Invoice'
            href={`/invoices/new?user_id=${user.id}`} />
          <AdminMenuItem
            menuKey='flag_user'
            label='Flag as important'
            onClick={() => this.setState({ modal: FLAG_USER_AS_IMPORTANT_DIALOG })} />
          <AdminMenuItem
            menuKey='feature_flags'
            label='Manage Feature Flags'
            href={`/users/${user.id}/feature_flags`} />
          <AdminMenuItem
            menuKey='referrals'
            label='Referrals'
            href={`/users/${user.id}/referrals`} />
          <MainCol>
            <UserBannerCard
              user={user}
              subscription={subscription}
              showDialog={this.showDialog} />

            <h2 data-heading-alt><Msg capitalized m={commonNouns.stats} /></h2>
            <StatsWidget
              defaultInterval={DEFAULT_STATS_INTERVAL}
              initialStats={careerStats}
              user={user}
              persistInterval={() => {}} />

            <h2 data-heading-alt><T id='UserProfilePage.recentRR' defaultMessage='Recent Routes & Rides' /></h2>
            <Card noPadding>
              {currentUserIsFriend || currentUser.id === user.id
                ? <RouteRideList
                  user={user}
                  limit={20}
                  onResults={this.onTRouteResults}
                  initialTRoutes={troutes}
                  onDeleteResult={this.onDeleteResult}
                  owned={currentUserIsUser} />
                : <>
                  {recentTRoutes.results.map(r => <TRouteItem r={r} key={r.name + r.id} />)}
                  {recentTRoutes.results.length === 0 && <IntroWidget large i='md-bicycle'>
                    <T id='UserProfilePage.noPublic' defaultMessage="This user doesn't have any public routes or rides." />
                  </IntroWidget>}
                </>}
            </Card>
          </MainCol>

          <SecondaryCol>
            {orgs.length > 0 && <>
              <h2 data-heading-alt>
                <T
                  id='UserProfilePage.memberOf'
                  defaultMessage='Member Of'
                  description='Heading for a card containing organizations the user belongs to.' />
              </h2>
              <Card>
                {orgs.map(org => (
                  <a href={org.url} key={org.url} className={styles.orgLink}>
                    <img src={org.logo}
                      width={44}
                      height={44} />
                    <div>
                      <div className={styles.orgName}>{org.name}</div>
                      {org.location && <div className={styles.orgLocation}>{org.location}</div>}
                    </div>
                  </a>
                ))}
              </Card>
            </>}
            {goals.size > 0 && <>
              <h2 data-heading-alt><Msg m={featureNames.goals} /></h2>
              <Card>
                {goals.valueSeq().map(gp => <MiniGoalCard gp={gp} key={gp.get('id')} />)}
              </Card>
            </>}
            {(followers.size + following.size > 0) && <>
              <h2 data-heading-alt><Msg m={messages.follows} /></h2>
              <FollowsCard
                defaultTo='followers'
                following={following}
                followers={followers}
                user={user}
                currentUser={currentUser}
                interactive={false} // no interactivity on profile page
              />
            </>}
            <h2 data-heading-alt><T id='UserProfilePage.recentPhotos' defaultMessage='Recent Photos' /></h2>
            <Card>
              {/* TODO - figure out how to up-size these when we collpase to mobile view? */}
              {photos.length > 0 && <div className={styles.photoGrid}>
                {photos.slice(0, 8).map(p => <Image
                  gallery={{ parentType: 'user', parentId: user.id, activePhotoId: p.id, limit: 30 }}
                  className={styles.recentPhoto}
                  key={p.id}
                  id={p.id}
                  height={114}
                  width={114} />)}
              </div>}
              {photos.length === 0 && <IntroWidget i='ios-image'>
                <T
                  id='UserProfilePage.noPhotos'
                  defaultMessage='This user has not uploaded any photos' />
              </IntroWidget>}
            </Card>
          </SecondaryCol>
        </ColWrap>
      </>
    )
  }
}

const loadRecentTRoutes = userId => apiv3.search({
  userId,
  sortBy: 'created DESC',
  limit: 20,
  types: ['routes', 'trips']
})

const loadInitialData = (userId, props) => {
  const statsInterval = DEFAULT_STATS_INTERVAL
  const statsIntervalValue = StatsWidget.getIntervalValue(statsInterval, 0)

  const promises = {
    _: props.loadUser(userId),
    __: props.loadFollows({ userId, extras: 'users' }),
    careerStats: getCareerStats(userId, statsInterval, statsIntervalValue),
    orgs: fetchJSON(`/users/${userId}/memberships.json`).then(({ results }) => results),
    recentTRoutes: loadRecentTRoutes(userId),
    goals: fetchJSON(`/users/${userId}/goals.json`).then(({ results }) => Map(results.map(r => [r.id, Immutable.fromJS(r)]))),
    photos: fetchJSON(`/users/${userId}/photos.json?limit=100`).then(({ photos }) => photos),
    isLiveLogging: fetchJSON(`/users/${userId}/is_live_logging.json`).then(({ is_live_logging: isLiveLogging }) => isLiveLogging)
  }
  if (props.currentUser.id) {
    promises['___'] = props.loadFollows({ userId: props.currentUser.id, extras: 'users' })
  }

  return promiseMapAll(promises)
}

const LoadableUserProfilePage = ({ userId, ...props }) => {
  return <Loadable
    key={userId}
    {...props}
    component={_UserProfilePage}
    errorComponent={(e) => (
      (_.get(e, 'errors[0].error_code') === 'resource_private')
        ? <PrivateUserProfilePage />
        : <FriendsOnlyUserProfilePage
          currentUser={props.currentUser}
          userId={userId}
        />
    )}
    matchesErrorCondition={(resourceError) => {
      const code = _.get(resourceError, 'errors[0].error_code') || _.get(resourceError, 'details[0].error_code')
      return (code === 'resource_private' || code === 'resource_friends_only')
    }}
    resourceLoaded={() => loadInitialData(userId, props)} />
}

const mapStateToProps = (state, ownProps) => {
  const { currentUser } = state
  const { userId } = ownProps
  return {
    currentUser,
    user: userSelectors.find(state, userId),
    users: userIds => userSelectors.findByIds(state, userIds),
    subscription: subscriptionSelectors.byUserId(state, userId),
    following: followSelectors.followsForUser(state, userId),
    followers: followSelectors.byItem(state, userId, 'User'),
    currentUserIsFriend: followSelectors.byUserAndItem(state, userId, currentUser.id, 'User')?.level === 'friend'
  }
}

const mapDispatchToProps = {
  loadUser: userActions.read,
  loadFollows: followActions.list
}

export const UserProfilePage = withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableUserProfilePage))
